import { GetStaticProps } from 'next'
import { useInfiniteQuery } from '@tanstack/react-query'

import Button from '@/atoms/Button/Button'
import BlogCard from '@/molecules/BlogCard/BlogCard'
import Layout from '@/templates/Layout/Layout'
import CategoryHero from '@/templates/BlogHero/CategoryHero'
import { fetchListingData } from '@/utils/api/posts'
import { TEN_MINS_REVALIDATION } from '@/utils/constants/revalidation'

interface Props {
  data: BlogListingResponse
  categorySlug: string
  category?: BlogPostCategory
  mostReadPosts: BlogPostList[]
}

export default function PepperSpotlightPage({
  data,
  categorySlug,
  category,
  mostReadPosts
}: Props) {
  const {
    data: blogListingData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(
    ['blog posts', data.posts.pageInfo.startCursor, categorySlug],
    ({ pageParam }) =>
      fetchListingData({
        limit: 10,
        after: pageParam,
        categoryName: categorySlug,
        fetchFeaturedPost: false
      }),
    {
      enabled: false,
      refetchOnWindowFocus: false,
      cacheTime: Infinity,
      initialData: () => {
        return {
          pageParams: [undefined, 1],
          pages: [data]
        }
      },
      getNextPageParam: lastPage =>
        lastPage.posts.pageInfo.hasNextPage
          ? lastPage.posts.pageInfo.endCursor
          : false
    }
  )

  const postGroup = blogListingData?.pages.map(data => data.posts.nodes) ?? []
  const posts = ([] as BlogPostList[]).concat(...postGroup)

  const metadata = [
    {
      property: 'og:title',
      content: `${category?.name ?? ''} | Pepper Content`
    },
    {
      property: 'description',
      content: category?.description ?? ''
    },
    {
      property: 'og:description',
      content: category?.description ?? ''
    }
  ]

  return (
    <div>
      <Layout
        title={`${category?.name ?? ''} | Pepper Content`}
        meta={metadata}
        canonical="/pepper-spotlight/"
      >
        <CategoryHero
          background="linear-gradient(180deg, #F6F1FD 0%, #FFF5F9 100%)"
          backgroundImage="/assets/pepper-spotlight/images/pepper-spotlight.svg"
          textColorVariable="purple-80"
          title={category?.name}
          description={category?.description}
          footerComponent={
            <span>
              Listen on&nbsp;
              <a
                href="https://open.spotify.com/show/4uJtInqvDKKyEsJ5YfnS9l"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="w-10 inline-block  |  align-middle  |  p-1"
                  src="/assets/pepper-spotlight/icons/social/spotify.svg"
                  alt="Spotify"
                />
              </a>
              <a
                href="https://podcasts.apple.com/in/podcast/pepper-spotlight/id1546200551"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="w-10 inline-block  |  align-middle  |  p-1"
                  src="/assets/pepper-spotlight/icons/social/apple-podcasts.svg"
                  alt="Apple Podcast"
                />
              </a>
              <a
                href="https://feeds.buzzsprout.com/1818227.rss"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="w-10 inline-block  |  align-middle  |  p-1"
                  src="/assets/pepper-spotlight/icons/social/rss.svg"
                  alt="RSS Feed"
                />
              </a>
            </span>
          }
        />

        <div className="max-desktop-width mx-auto pt-6 pb-8 lg:pt-10 lg:pb-12 px-5 sm:px-10 lg:px-5">
          <section className="grid grid-cols-1 lg:grid-cols-12">
            <article className="lg:col-span-6  |  mb-10 lg:mb-11">
              <p className="text-paragraph-4 font-medium  |  mb-4">
                Featured Post
              </p>

              <BlogCard
                type="featured"
                date={posts[0].date}
                description={posts[0].excerpt}
                image={
                  posts[0].featuredImage
                    ? {
                        url: posts[0].featuredImage.node.sourceUrl,
                        altText: posts[0].featuredImage.node.altText,
                        width: posts[0].featuredImage.node.mediaDetails.width,
                        height: posts[0].featuredImage.node.mediaDetails.height
                      }
                    : undefined
                }
                alt={posts[0].featuredImage?.node.altText}
                slug={`/blog/${posts[0].slug}/`}
                categories={posts[0].categories.nodes}
                title={posts[0].title}
                isFirstFold={true}
              />
            </article>
            {/* spacer col */}
            <div className="hidden lg:block  |  lg:col-span-1" />
            {mostReadPosts.length > 0 && (
              <section className="lg:col-span-5  |  mb-10 lg:mb-11">
                <p className="text-paragraph-4 font-medium  |  mb-4">
                  Most Read
                </p>
                <div className="space-y-6">
                  {mostReadPosts.map(post => (
                    <article key={post.id}>
                      <BlogCard
                        type="minimal"
                        date={post.date}
                        description={post.excerpt}
                        image={
                          post.featuredImage
                            ? {
                                url: post.featuredImage.node.sourceUrl,
                                altText: post.featuredImage.node.altText,
                                width:
                                  post.featuredImage.node.mediaDetails.width,
                                height:
                                  post.featuredImage.node.mediaDetails.height
                              }
                            : undefined
                        }
                        alt={post.featuredImage?.node.altText}
                        slug={`/blog/${post.slug}/`}
                        categories={post.categories.nodes}
                        title={post.title}
                      />
                    </article>
                  ))}
                </div>
              </section>
            )}
          </section>

          <section
            id="js-blog-list"
            className="grid gap-x-5 gap-y-8 md:gap-y-10 md:gap-x-8 md:grid-cols-12  |  mb-12 lg:mb-16"
          >
            {posts.map((post, index) => {
              if (index === 0) return
              return (
                <article
                  key={post.id}
                  className="col-span-1 md:col-span-6 lg:col-span-4 "
                >
                  <BlogCard
                    type="default"
                    date={post.date}
                    description={post.excerpt}
                    image={
                      post.featuredImage
                        ? {
                            url: post.featuredImage.node.sourceUrl,
                            altText: post.featuredImage.node.altText,
                            width: post.featuredImage.node.mediaDetails.width,
                            height: post.featuredImage.node.mediaDetails.height
                          }
                        : undefined
                    }
                    alt={post.featuredImage?.node.altText}
                    slug={`/blog/${post.slug}/`}
                    categories={post.categories.nodes}
                    title={post.title}
                  />
                </article>
              )
            })}
          </section>

          {hasNextPage && (
            <div className="text-center">
              <Button
                type="secondary"
                onClick={() => fetchNextPage()}
                loading={isFetchingNextPage}
              >
                Load More
              </Button>
            </div>
          )}
        </div>
      </Layout>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async () => {
  const categorySlug = 'pepper-spotlight'

  const result = await fetchListingData({
    limit: 10,
    categoryName: categorySlug,
    fetchFeaturedPost: false,
    mostReadTag: `most-read-${categorySlug}`
  })

  return {
    props: {
      data: result,
      posts: result.posts.nodes,
      categorySlug,
      category: result?.category,
      mostReadPosts: result.mostReadPosts?.nodes ?? []
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}
